import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, ChevronRight, CircleHelp, Fish, LogOut, UserRound } from "lucide-react";
import { Button } from "../components/Button";
import { session as authSession, signOut } from "../../auth";
import {
  cacheServerSessions,
  listOfflineSessions,
  readCachedServerSessions,
  useOnlineStatus,
} from "../lib/offline-session";
import { formatDateTime } from "../lib/format";

type MemberCohort = "founding" | "beta" | "standard";
type MemberProfile = {
  name: string;
  email: string;
  member_cohort: MemberCohort;
};

function memberLabel(cohort: MemberCohort | undefined) {
  if (cohort === "founding") return "Founding Member";
  if (cohort === "beta") return "Beta Member";
  return "Member";
}

function memberBadgeClass(cohort: MemberCohort | undefined) {
  if (cohort === "founding")
    return "border-accent/50 bg-accent/10 text-accent";
  if (cohort === "beta")
    return "border-border bg-inset text-primary";
  return "border-border bg-background text-secondary";
}

function autoCloseAt(session: any) {
  if (!session?.planned_end_at) return null;
  return new Date(new Date(session.planned_end_at).getTime() + 60 * 60 * 1000);
}
function minutesUntil(date: Date | null, now: number) {
  if (!date) return null;
  return Math.max(0, Math.ceil((date.getTime() - now) / 60_000));
}

export default function HomeSimplePage() {
  const online = useOnlineStatus();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useQuery({ queryKey: ["session"], queryFn: () => authSession.get() });
  const memberQuery = useQuery<MemberProfile>({
    queryKey: ["member_profile"],
    queryFn: async () => {
      const response = await fetch("/app-api/member/me", { cache: "no-store" });
      if (!response.ok) throw new Error("Could not load member profile");
      return response.json();
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
  const sessionsQuery = useQuery({
    queryKey: ["carpintel_sessions"],
    queryFn: async () => {
      try {
        const response = await fetch("/app-api/sessions");
        if (!response.ok) throw new Error("Could not load sessions");
        const rows = await response.json(); cacheServerSessions(rows); return rows;
      } catch (error) {
        const cached = readCachedServerSessions(); const local = listOfflineSessions();
        if (cached.length || local.length || !navigator.onLine) return [...local, ...cached];
        throw error;
      }
    },
    refetchInterval: online ? 60_000 : false,
  });

  const sessions: any[] = sessionsQuery.data ?? [];
  const active = useMemo(() => sessions.find((session) => !session.end_at) ?? null, [sessions]);
  const needsReview = useMemo(() => sessions.find((session) => session.auto_ended && !session.auto_end_reviewed_at) ?? null, [sessions]);
  const closeAt = autoCloseAt(active);
  const closeMinutes = minutesUntil(closeAt, now);
  const inEndWarning = closeMinutes !== null && closeMinutes <= 15;
  const firstSession = !sessionsQuery.isLoading && sessions.length === 0;
  const member = memberQuery.data;

  return (
    <div className="mx-auto max-w-[900px] space-y-5 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">CatchCompass</p>
          {member ? (
            <div className="mt-2">
              <p className="truncate text-sm font-semibold text-primary">{member.name}</p>
              <span className={`mt-1 inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${memberBadgeClass(member.member_cohort)}`}>
                {memberLabel(member.member_cohort)}
              </span>
            </div>
          ) : null}
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-primary">{active ? "You're fishing" : "What do you want to do?"}</h1>
          <p className="mt-1 text-sm text-secondary">{active ? "Your live session is the priority. Everything else can wait." : "Start a session now, or plan a future trip."}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2"><Button asChild size="sm" variant="tertiary"><Link to="/account" aria-label="Account"><UserRound className="size-4" /></Link></Button><Button size="sm" variant="tertiary" onClick={signOut} aria-label="Sign out"><LogOut className="size-4" /></Button></div>
      </header>

      {!online ? <div className="rounded-xl border border-accent/40 bg-inset p-3 text-sm leading-6 text-secondary">You're offline. A live session can still be recorded on this phone and will sync when signal returns.</div> : null}

      {needsReview && !active ? (
        <section className="rounded-2xl border border-warning bg-warning-weak p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-warning">Session needs a quick check</p>
          <h2 className="mt-1 font-semibold text-primary">{needsReview.lake_name || "Previous session"}{needsReview.peg ? ` · ${needsReview.peg}` : ""}</h2>
          <p className="mt-1 text-sm leading-6 text-secondary">CatchCompass closed this session automatically. Confirm when you actually finished so the exposure data stays honest.</p>
          <Button asChild className="mt-3"><Link to={`/sessions/${needsReview.id}/review-auto-end`}>Review finish time</Link></Button>
        </section>
      ) : null}

      {active ? (
        <section className={`rounded-2xl border p-5 ${inEndWarning ? "border-warning bg-warning-weak" : "border-accent/50 bg-raised"}`}>
          <div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className={`text-xs font-semibold uppercase tracking-wider ${inEndWarning ? "text-warning" : "text-accent"}`}>Current session</p><h2 className="mt-1 truncate text-xl font-bold text-primary">{active.lake_name || active.lake || "Fishing session"}</h2><p className="mt-1 text-sm text-secondary">{[active.peg, active.target_species ? `Target: ${active.target_species}` : ""].filter(Boolean).join(" · ")}</p><p className="mt-1 text-xs text-secondary">Started {formatDateTime(active.start_at)}</p></div><Fish className="size-6 shrink-0 text-accent" /></div>
          {closeAt ? <div className={`mt-4 rounded-lg border p-3 text-sm ${inEndWarning ? "border-warning text-warning" : "border-border bg-background text-secondary"}`}>{inEndWarning ? <strong>Session auto-closes in about {closeMinutes} min unless you extend it.</strong> : <span>Planned finish {formatDateTime(active.planned_end_at)} · automatic close {formatDateTime(closeAt.toISOString())}</span>}</div> : null}
          <Button asChild size="lg" className="mt-4 w-full"><Link to={String(active.id).startsWith("offline-") ? `/sessions/${active.id}` : `/live/${active.id}`}>Resume fishing <ChevronRight /></Link></Button>
        </section>
      ) : (
        <Button asChild size="lg" className="min-h-20 w-full rounded-2xl px-5 text-left">
          <Link to="/scout" className="flex min-w-0 items-center gap-3">
            <span className="min-w-0 flex-1 whitespace-normal"><span className="block text-lg font-bold">Start Fishing</span><span className="mt-1 block whitespace-normal break-words text-sm font-normal leading-5 opacity-90">Choose the water, choose or scout your peg, then build the plan.</span></span>
            <ChevronRight className="shrink-0" />
          </Link>
        </Button>
      )}

      <Button asChild size="lg" variant="secondary" className="min-h-16 w-full rounded-2xl px-5 text-left"><Link to="/advisor?mode=future" className="flex min-w-0 items-center gap-3"><span className="min-w-0 flex-1"><span className="flex items-center gap-2 font-bold"><CalendarClock className="size-4" /> Get future advice</span><span className="mt-1 block whitespace-normal text-sm font-normal text-secondary">Plan a trip before you get to the lake.</span></span><ChevronRight className="shrink-0" /></Link></Button>

      {firstSession ? <section className="rounded-xl border border-border bg-raised p-4"><div className="flex items-start gap-3"><CircleHelp className="mt-0.5 shrink-0 text-accent" /><div><h2 className="font-semibold text-primary">Your first session</h2><p className="mt-1 text-sm leading-6 text-secondary">You do not need to set CatchCompass up first. Tap Start Fishing, choose the water and peg, then start. Once fishing, only tell CatchCompass when something changes.</p></div></div></section> : null}
      {sessionsQuery.error ? <p className="rounded-lg border border-error bg-error-weak p-3 text-sm text-error">Your latest session status could not be refreshed. Try again when you have signal.</p> : null}
    </div>
  );
}
